import React from "react";
import { WebPaths } from "../../../../../routes/data";
import { BsUiChecks } from "react-icons/bs";
import {
  UserCircleIcon,
  ClipboardDocumentCheckIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  DocumentArrowUpIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import Button from "../../../components/ui/Button";

const features = [
  {
    name: "Create Your Profile",
    description: "Sign up on our platform to get started.",
    icon: UserCircleIcon,
    step: "01",
  },
  {
    name: "Check Your Eligibility",
    description:
      "Answer a series of questions to confirm if you are eligible for free.",
    icon: ClipboardDocumentCheckIcon,
    step: "02",
  },
  {
    name: "Fill Out Application",
    description:
      "Our system guides you through the process and checks for errors automatically.",
    icon: PencilSquareIcon,
    step: "03",
  },
  {
    name: "Risk Analysis",
    description:
      "Our AI tool analyzes your application and offers improvement tips.",
    icon: ShieldCheckIcon,
    step: "04",
  },
  {
    name: "Upload Documents",
    description:
      "Get a customized checklist and upload your required documents safely.",
    icon: DocumentArrowUpIcon,
    step: "05",
  },
  {
    name: "Expert Reviews",
    description:
      "Have your application reviewed and submitted by immigration experts.",
    icon: UserGroupIcon,
    step: "06",
  },
  {
    name: "Interview Prep",
    description:
      "Practice with FiBi AI to build confidence for your interview.",
    icon: ChatBubbleLeftRightIcon,
    step: "07",
  },
];

const HowItWorks = () => {
  return (
    <section className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-3xl font-light font-spartan tracking-tight text-black-1a sm:text-4xl">
            <span className="text-blue-00 italic mr-2">How</span>
            it works
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            A simple, guided process from start to finish.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="relative bg-white-f9 rounded-2xl p-6 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="absolute top-4 right-4 text-4xl font-bold text-gray-100 group-hover:text-blue-50 transition-colors font-spartan">
                {feature.step}
              </div>

              <div className="h-12 w-12 rounded-xl bg-blue-00/10 flex items-center justify-center text-blue-00 mb-4 group-hover:scale-110 transition-transform duration-300">
                <feature.icon className="h-6 w-6" aria-hidden="true" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {feature.name}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}

          {/* CTA Card */}
          <div className="relative bg-blue-00 rounded-2xl p-6 flex flex-col items-center justify-center text-center shadow-lg hover:shadow-xl transition-shadow">
            <h3 className="text-xl font-bold text-white mb-6">
              Ready to start?
            </h3>
            <div className="w-full">
              <Button
                label="Check Eligibility"
                onClick={() => {
                  window.location.href = `${WebPaths.checkEligibility}/ds160`;
                }}
                variant="secondary"
                iconHoverStyle="scale"
                size="large"
                startIcon={<BsUiChecks className="text-lg" />}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
